import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Tabs, Tab, Card, CardContent, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, Chip, FormControlLabel, Switch, Grid
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon,
  Save as SaveIcon, Cancel as CancelIcon
} from '@mui/icons-material';
import axios from 'axios';
import TargetsManagement from '../components/TargetsManagement';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';

const categoryLabels = {
  roles: 'Roles',
  channels: 'Channels',
  contentTypes: 'Content',
  genres: 'Genres',
  statuses: 'Status',
  videoPerformances: 'V Performance',
  uploadStatuses: 'Upload Status',
  expenseCategories: 'Expense Categories',
  paymentMethods: 'Payment Methods',
  targets: '🎯 Targets',
  shoot_day_limits: '🎬 Shoot Limits',
  propertyTypes: '🏢 Property Type',
  propertyStatuses: '🏢 Property Status',
  ownershipTypes: '🏢 Ownership',
  itemConditions: '🏢 Item Condition',
  editor_work_types: '✂️ Editor Work Types',
  director_work_types: '🎬 Director Work Types'
};

const LIMIT_RULE_LABELS = {
  long_video_only: 'Long Video only (per day)',
  shorts_only:     'Shorts only (per day)',
  mixed:           'Mixed day (Long + Shorts)',
  ad_content_only: 'Ad Content only (per day)',
};

function ShootLimitsEditor({ items, onSaved, apiUrl }) {
  const [limits, setLimits] = useState({
    long_video_only: { maxLong: 2, maxShorts: 0, maxAd: 0 },
    shorts_only:     { maxLong: 0, maxShorts: 10, maxAd: 0 },
    mixed:           { maxLong: 1, maxShorts: 6, maxAd: 0 },
    ad_content_only: { maxLong: 0, maxShorts: 0, maxAd: 2 },
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!items?.length) return;
    const parsed = { ...limits };
    items.forEach(item => {
      try {
        const data = typeof item.icon === 'string' ? JSON.parse(item.icon) : item.icon;
        if (data?.key && parsed[data.key]) parsed[data.key] = data;
      } catch (_) {}
    });
    setLimits(parsed);
  }, [items]);

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const [key, rule] of Object.entries(limits)) {
        const existing = items.find(i => {
          try { return (typeof i.icon === 'string' ? JSON.parse(i.icon) : i.icon)?.key === key; } catch (_) { return false; }
        });
        const payload = { ...rule, key, label: LIMIT_RULE_LABELS[key] };
        if (existing) {
          await axios.put(`${apiUrl}/dropdowns/admin/values/${existing.id}`, { icon: JSON.stringify(payload) });
        } else {
          await axios.post(`${apiUrl}/dropdowns/admin/values`, {
            category: 'shoot_day_limits', value: key,
            icon: JSON.stringify(payload), displayOrder: 0, isActive: true
          });
        }
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      onSaved();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Configure how many shoots of each type are allowed on a single day. These limits are used by the Auto-Propose engine and shown as warnings in the Shoot Planner.
      </Typography>
      {saved && <Alert severity="success" sx={{ mb: 2 }}>Shoot limits saved successfully!</Alert>}
      <Grid container spacing={2}>
        {Object.entries(limits).map(([key, rule]) => (
          <Grid item xs={12} sm={6} key={key}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                  {LIMIT_RULE_LABELS[key]}
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  {key !== 'shorts_only' && key !== 'ad_content_only' && (
                    <TextField size="small" label="Max Long Videos" type="number"
                      inputProps={{ min: 0, max: 10 }}
                      value={rule.maxLong ?? 0}
                      onChange={e => setLimits(l => ({ ...l, [key]: { ...l[key], maxLong: parseInt(e.target.value) || 0 } }))}
                      sx={{ width: 140 }} />
                  )}
                  {key !== 'long_video_only' && key !== 'ad_content_only' && (
                    <TextField size="small" label="Max Shorts" type="number"
                      inputProps={{ min: 0, max: 20 }}
                      value={rule.maxShorts ?? 0}
                      onChange={e => setLimits(l => ({ ...l, [key]: { ...l[key], maxShorts: parseInt(e.target.value) || 0 } }))}
                      sx={{ width: 140 }} />
                  )}
                  {key === 'ad_content_only' && (
                    <TextField size="small" label="Max Ad Content" type="number"
                      inputProps={{ min: 0, max: 10 }}
                      value={rule.maxAd ?? 0}
                      onChange={e => setLimits(l => ({ ...l, [key]: { ...l[key], maxAd: parseInt(e.target.value) || 0 } }))}
                      sx={{ width: 140 }} />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Box mt={2}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Shoot Limits'}
        </Button>
      </Box>
    </Box>
  );
}

function Settings() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [dropdowns, setDropdowns] = useState({});
  const [targets, setTargets] = useState([]);
  const [channels, setChannels] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({ value: '', displayOrder: 0, isActive: true, icon: '👤', color: 'default' });
  const [targetFormData, setTargetFormData] = useState({ channel: '', contentType: '', targetType: 'weekly', targetCount: 0, isActive: true });
  const [alert, setAlert] = useState(null);

  const categories = Object.keys(categoryLabels);
  const currentCategory = categories[selectedTab];

  useEffect(() => {
    fetchDropdowns();
  }, []);

  const fetchTargets = async () => {
    // Targets are fetched by the TargetsManagement component
  };

  const fetchDropdowns = async () => {
    try {
      // Fetch from the admin endpoint which includes IDs and full metadata
      const response = await axios.get(`${API_URL}/dropdowns/admin/all`);
      console.log('Dropdowns admin response:', response.data.data);
      setDropdowns(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dropdowns:', error);
      setAlert({ type: 'error', message: 'Error loading data' });
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditItem(null);
    setFormData({ value: '', displayOrder: 0, isActive: true, icon: '👤', color: 'default', channelCode: '' });
    setOpenDialog(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);

    // For roles, extract the actual value from metadata
    if (currentCategory === 'roles' && item.metadata) {
      setFormData({
        value: item.metadata.label || item.value,
        icon: item.metadata.icon || '👤',
        color: item.metadata.color || 'default',
        displayOrder: item.displayOrder,
        isActive: item.isActive
      });
    } else if (currentCategory === 'channels') {
      let channelCode = '';
      if (item.icon) {
        try { channelCode = JSON.parse(item.icon).code || ''; } catch (_) {}
      }
      setFormData({ value: item.value, displayOrder: item.displayOrder, isActive: item.isActive, channelCode });
    } else {
      setFormData({
        value: item.value,
        displayOrder: item.displayOrder,
        isActive: item.isActive
      });
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      let dataToSave = { ...formData };

      // Special handling for channels - store code in icon field as JSON
      if (currentCategory === 'channels') {
        dataToSave.icon = JSON.stringify({ code: (formData.channelCode || '').toUpperCase().trim() });
      }

      // Special handling for roles - store as JSON
      if (currentCategory === 'roles' && !editItem) {
        // For new roles, create JSON structure
        const roleData = {
          value: formData.value.toLowerCase().replace(/\s+/g, ''),
          label: formData.value,
          icon: formData.icon || '👤',
          color: formData.color || 'default'
        };
        dataToSave.value = JSON.stringify(roleData);
      }
      
      if (editItem) {
        // Update existing
        await axios.put(`${API_URL}/dropdowns/admin/values/${editItem.id}`, dataToSave);
        setAlert({ type: 'success', message: 'Updated successfully' });
      } else {
        // Create new
        await axios.post(`${API_URL}/dropdowns/admin/values`, {
          category: currentCategory,
          ...dataToSave
        });
        setAlert({ type: 'success', message: 'Added successfully' });
      }
      setOpenDialog(false);
      fetchDropdowns();
    } catch (error) {
      console.error('Error saving:', error);
      setAlert({ 
        type: 'error', 
        message: error.response?.data?.message || 'Error saving data' 
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this item? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API_URL}/dropdowns/admin/values/${id}`);
      setAlert({ type: 'success', message: 'Deleted successfully' });
      fetchDropdowns();
    } catch (error) {
      console.error('Error deleting:', error);
      setAlert({ type: 'error', message: error.response?.data?.message || 'Error deleting item' });
    }
  };

  // Process current items - admin endpoint returns full objects with IDs
  let currentItems = dropdowns[currentCategory] || [];
  
  // Special handling for roles - parse JSON value field
  if (currentCategory === 'roles' && Array.isArray(currentItems)) {
    currentItems = currentItems.map((role) => {
      try {
        const parsed = JSON.parse(role.value);
        return {
          id: role.id,
          value: `${parsed.icon} ${parsed.label}`,
          displayValue: parsed.value,
          displayOrder: role.displayOrder,
          isActive: role.isActive,
          metadata: parsed
        };
      } catch (e) {
        return role;
      }
    });
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>
          ⚙️ Configure
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          size="small"
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Add New {categoryLabels[currentCategory]}
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
            Add
          </Box>
        </Button>
      </Box>

      {alert && (
        <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ mb: 3 }}>
          {alert.message}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Tabs 
            value={selectedTab} 
            onChange={(e, newValue) => setSelectedTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider', 
              mb: 3,
              '& .MuiTabs-flexContainer': {
                gap: { xs: 0, sm: 1 }
              }
            }}
            TabIndicatorProps={{
              style: { display: 'flex' }
            }}
          >
            {categories.map((category, index) => (
              <Tab 
                key={category} 
                label={categoryLabels[category]}
                sx={{ 
                  textTransform: 'none', 
                  fontWeight: selectedTab === index ? 700 : 400,
                  minWidth: { xs: '33.33%', sm: 'auto' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  px: { xs: 1, sm: 2 }
                }}
              />
            ))}
          </Tabs>

          {currentCategory === 'targets' ? (
            <TargetsManagement />
          ) : currentCategory === 'shoot_day_limits' ? (
            <ShootLimitsEditor
              items={dropdowns['shoot_day_limits'] || []}
              onSaved={fetchDropdowns}
              apiUrl={API_URL}
            />
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Value</strong></TableCell>
                  {currentCategory === 'channels' && <TableCell width={80}><strong>Code</strong></TableCell>}
                  <TableCell width={120} sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>Order</strong></TableCell>
                  <TableCell width={100}><strong>Status</strong></TableCell>
                  <TableCell width={120} align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={currentCategory === 'channels' ? 5 : 4} align="center">
                      <Typography color="text.secondary">No items found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  currentItems.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.value}</TableCell>
                      {currentCategory === 'channels' && (
                        <TableCell>
                          {(() => { try { return JSON.parse(item.icon || '{}').code || '—'; } catch (_) { return '—'; } })()}
                        </TableCell>
                      )}
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{item.displayOrder}</TableCell>
                      <TableCell>
                        <Chip 
                          label={item.isActive ? 'Active' : 'Inactive'}
                          color={item.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          size="small" 
                          onClick={() => handleEdit(item)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDelete(item.id)}
                          color="error"
                          disabled={!item.isActive}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

              <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                Total: {currentItems.length} {categoryLabels[currentCategory]}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editItem ? 'Edit' : 'Add New'} {categoryLabels[currentCategory]}
        </DialogTitle>
        <DialogContent>
          <Box pt={2} display="flex" flexDirection="column" gap={3}>
            <TextField
              label={currentCategory === 'roles' ? 'Role Name' : 'Value'}
              fullWidth
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              required
              placeholder={currentCategory === 'roles' ? 'e.g., Producer' : ''}
            />
            {currentCategory === 'channels' && (
              <TextField
                label="Channel Code (3-4 letters)"
                fullWidth
                value={formData.channelCode || ''}
                onChange={(e) => setFormData({ ...formData, channelCode: e.target.value.toUpperCase().slice(0, 4) })}
                placeholder="e.g., SCHN"
                helperText="3-4 letter code used for Content ID generation (e.g. SCHN → SCHN-SH-240826-001)"
                inputProps={{ maxLength: 4 }}
              />
            )}
            {currentCategory === 'roles' && (
              <>
                <TextField
                  label="Icon (Emoji)"
                  fullWidth
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="e.g., 🎪"
                  helperText="Enter an emoji to represent this role"
                />
                <TextField
                  label="Color"
                  fullWidth
                  select
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                >
                  <option value="default">Default</option>
                  <option value="primary">Primary (Blue)</option>
                  <option value="secondary">Secondary (Purple)</option>
                  <option value="error">Error (Red)</option>
                  <option value="warning">Warning (Orange)</option>
                  <option value="info">Info (Light Blue)</option>
                  <option value="success">Success (Green)</option>
                </TextField>
              </>
            )}
            <TextField
              label="Display Order"
              type="number"
              fullWidth
              value={formData.displayOrder}
              onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            startIcon={<SaveIcon />}
            disabled={!formData.value.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Settings;
